import { UserManager, User, WebStorageStateStore } from "oidc-client-ts";
import type { SignoutRedirectArgs, UserManagerSettings } from "oidc-client-ts";
import { store } from "@/store";
import { session_storage, storage } from "./storage";
import { assignWith } from "lodash";

export class OpenIdConnectService {
  private config: UserManagerSettings;

  private userManager: UserManager;

  // 默认配置
  private static defaultConfig: UserManagerSettings = {
    // OIDC/OAuth2 提供者的 URL
    // authority: "https://testlive.zfoline.net:8018/ZhuoFan_Wb_OAuthService_WebApi/",
    authority: import.meta.env.VITE_OIDC_AUTHORITY + "/ZhuoFan_Wb_OAuthService_WebApi/", // 从环境变量中获取
    // 客户端应用程序的标识符（从服务端接口获取）
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
    // OIDC 提供者所需的响应类型 默认值:'id_token'
    response_type: "code",
    // OIDC 提供者规定的请求范围
    scope: "openid profile idcard userfeature",
    // 登录系统地址 - 表示重定向 URI 认证服务器回调的客户端页面
    redirect_uri: "/signinOidc",
    // 客户端系统地址 - 得到新令牌后需要重定向的URL
    silent_redirect_uri: "/refreshOidc",
    // 登出系统地址
    post_logout_redirect_uri: "/logout",
    disablePKCE: true, // 禁用 PKCE 功能
    // 启用自动静默刷新token，用来在令牌过期前自动更新令牌
    automaticSilentRenew: true,
    // 是否在用户在 OP 处执行注销时引发事件，默认为 true。
    monitorSession: true,
    // 是否过滤协议声明
    filterProtocolClaims: true,
    // 是否加载用户信息
    loadUserInfo: true,
    // 存储oidc.user到window.localStorage
    // userStore: new WebStorageStateStore({ store: window.localStorage }),
  };

  constructor(option: Partial<UserManagerSettings>) {
    const newOption = assignWith(OpenIdConnectService.defaultConfig, option, (s, o) => o ?? s);
    const { redirect_uri, silent_redirect_uri, post_logout_redirect_uri } = newOption;
    const origin = window.location.origin;
    const assignOption = {
      redirect_uri: origin + redirect_uri,
      silent_redirect_uri: origin + silent_redirect_uri,
      post_logout_redirect_uri: origin + post_logout_redirect_uri,
    };
    this.config = { ...newOption, ...assignOption };
    this.userManager = store.state.userManager || new UserManager(this.config);

    store.commit("SET_DATA", { name: "userManagerConfig", data: this.config });
    store.commit("SET_DATA", { name: "userManager", data: this.userManager, saveStorage: false });

    this.initUserManager();
  }

  private initUserManager() {
    this.userManager.startSilentRenew();

    this.userManager.events.unload().then(() => {
      // console.log("触发unload");
      // this.setUser(undefined);
    });

    this.userManager.events.addUserLoaded((user) => {
      // console.log("OIDC加载用户信息", user);
      this.setUser(user as unknown as NewUser);
    });

    this.userManager.events.addUserUnloaded(() => {
      // console.log("触发addUserUnloaded");
      // this.setUser(undefined);
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log("addAccessTokenExpiring正在刷新");
    });

    this.userManager.events.addSilentRenewError((error) => {
      console.log("addSilentRenewError");
      this.setUser(undefined);
    });
  }

  public static userAvailable(): boolean {
    return !!this.userAvailable();
  }

  public static getuser(): NewUser {
    return store.state.user;
  }

  public static setuser(user: NewUser | undefined) {
    // console.log({ user });
    store.commit("SET_DATA", { name: "user", data: user });
    store.commit("SET_DATA", { name: "isLogin", data: !!user });
    store.commit("SET_DATA", { name: "token", data: user?.access_token });
    store.commit("SET_DATA", { name: "currentRegisterUser", data: user?.profile });
  }

  public get UserManager(): UserManager {
    return this.userManager;
  }

  // 获取用户登录状态boolean
  public async getUserAvailable(): Promise<boolean> {
    const user = await this.userManager.getUser();
    return !!user;
  }

  // 登录
  public triggerSignIn(): Promise<void> {
    return this.userManager.signinRedirect();
  }

  // 登录回调
  public handleCallback(): Promise<NewUser | undefined> {
    return this.userManager.signinCallback() as Promise<NewUser | undefined>;
  }

  // 刷新
  public handleSigninSilent(): Promise<NewUser | null> {
    return this.userManager.signinSilent() as Promise<NewUser | null>;
  }

  // 刷新回调
  public handleSilentCallback(): Promise<void> {
    return this.userManager.signinSilentCallback();
  }

  // 触发重定向注销流程，将用户重定向到认证服务器以注销会话。
  public triggerSignOut(args?: SignoutRedirectArgs): Promise<void> {
    this.clearStaleState();
    return this.userManager.signoutRedirect(args);
  }

  // 清除状态
  public clearStaleState(): void {
    this.userManager.clearStaleState();
    this.setUser(undefined);
  }

  private setUser(user: NewUser | undefined): void {
    OpenIdConnectService.setuser(user);
  }
}

/**
 * 创建或获取OIDC服务实例。
 *
 * 该函数检查Vuex store中是否存在OIDC服务实例（`oidcService`）。
 * 如果不存在，则使用提供的OIDC配置 (`oidcConfig`) 新建一个`OpenIdConnectService`实例，
 * 并将其保存至store中，同时设置`saveStorage`为`false`以指示不需持久化至浏览器存储。
 * 若实例已存在，则直接返回该实例。
 *
 * @returns {OpenIdConnectService} 返回当前的`OpenIdConnectService`实例。
 */
export function createOidcService(): OpenIdConnectService {
  const oidcService = store.state.oidcService || new OpenIdConnectService({});
  store.commit("SET_DATA", { name: "oidcService", data: oidcService, saveStorage: false });
  return oidcService;
}

/**
 * 触发OIDC身份验证登录过程。
 *
 * 此函数通过调用`createOidcService()`实例化OIDC服务，并进一步调用其`triggerSignIn()`
 * 方法来启动用户身份验证的流程。这通常会重定向用户到身份提供商的登录页面，
 * 完成认证后，用户将被重定向回应用并携带认证信息。
 *
 * @returns {void} 该函数不直接返回任何值，其主要作用是启动外部登录流程。
 */
export function doLogin(): void {
  // clearStaleState();
  // store.commit("SET_DATA", { name: "callbackUrl", data: window.location.href });
  createOidcService().triggerSignIn();
}

/**
 * 执行注销操作并清理存储数据，保留部分必要信息。
 *
 * 此函数首先触发OIDC身份验证服务的注销过程，然后清除浏览器的本地存储和cookie。
 * 之后，它会从原始存储状态中恢复一些必要的数据（如oidcConfig、allColumnList、noticeIdLocal）
 * 并重新提交到 Vuex store 中，以便在用户重新登录后能够恢复这些设置。
 *
 * @returns {void} 该函数不返回任何值。
 */
export function doLogout(args?: SignoutRedirectArgs): void {
  createOidcService()
    .triggerSignOut(args)
    .then(() => {});
  clearStaleState();
  localStorage.clear();
  sessionStorage.clear();
  console.log("用户已注销");
}

/**
 * 清除过时的状态信息并保留特定数据。
 *
 * 清除浏览器的本地存储和cookie，以帮助准备进行状态重置或用户登出后的清理工作。
 * 到store中，确保这些配置在下次应用启动时可用。
 *
 * @returns {void} 不直接返回任何值，主要作用是重置并清理应用状态。
 */
export function clearStaleState(): void {
  store.commit("SET_DATA", { name: "user", data: null });
  store.commit("SET_DATA", { name: "userManagerConfig", data: null });
  store.commit("SET_DATA", { name: "currentRegisterUser", data: null });
  store.commit("SET_DATA", { name: "isLogin", data: false });
  store.commit("SET_DATA", { name: "token", data: null });
  storage.removeKeyStartsWith("oidc.");
  session_storage.clear();
  storage.clearCookie();
}

export interface NewUser {
  id_token: string;
  session_state: string;
  access_token: string;
  token_type: string;
  scope: string;
  profile: Profile;
  expires_at: number;
}

export interface Profile {
  iss: string;
  exp: number;
  iat: number;
  aud: string;
  oi_au_id: string;
  sub: string;
  name: string;
  id: string;
  userName: string;
  cardId: string;
  createTime: string;
  birth: string;
  birthStr: string;
  ethnic: string;
  contactNumber: string;
  gender: number;
  genderStr: string;
  authChannelName: string;
  registeredUserFeature: RegisteredUserFeature;
  hasUserFeatureImage: boolean;
  realNameAuthLevel: number;
  isPerfectInfo: boolean;
  passwordChangeDateTime: string;
  hintChangePassword: boolean;
  enforceChangePassword: boolean;
}

export interface RegisteredUserFeature {
  features128: number[];
  features512: number[];
  features1024: number[];
  registeredUser: string;
  faceImageBase64Str: string;
  normalizationFaceImageBase64Str: string;
  cardId: string;
  score: number;
  vectorDimensions: number;
  id: string;
}
